import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faPen, 
  faPlus, 
  faTrash, 
  faColumns,
  faUsers,
  faUserCheck,
  faKey,
  faUserTimes
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import ConfirmModal from '../../../components/ConfirmModal';
import { perfilService } from '../../../services/perfilService';

const columns = [
  { Header: 'ID', accessor: 'id' },
  { 
    Header: 'Nome', 
    accessor: 'nome',
    Cell: (value, row) => (
      <div className="flex items-center gap-2">
        <span>{value}</span>
        <div className="flex gap-1">
          <Link
            to={`/seguranca/perfis/${row.id}`}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="Visualizar"
          >
            <FontAwesomeIcon icon={faEye} />
          </Link>
          <Link
            to={`/seguranca/perfis/${row.id}/edit`}
            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
            title="Editar"
          >
            <FontAwesomeIcon icon={faPen} />
          </Link>
        </div>
      </div>
    )
  },
  { Header: 'Descrição', accessor: 'descricao' },
  { 
    Header: 'Nível de Acesso', 
    accessor: 'nivel_acesso',
    Cell: (value) => (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
        Nível {value}
      </span>
    )
  },
  { 
    Header: 'Status', 
    accessor: 'ativo',
    Cell: (value) => (
      <span className={`px-2 py-1 rounded-full text-xs ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? 'Ativo' : 'Inativo'}
      </span>
    )
  },
  { 
    Header: 'Criado em', 
    accessor: 'created_at',
    Cell: (value) => {
      if (!value) return '';
      const date = new Date(value);
      const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      return adjustedDate.toLocaleDateString('pt-BR');
    }
  }
];

const pageSizes = [5, 10, 20];

const PerfilsList = () => {
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, ativos: 0, inativos: 0 });
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modais
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [perfilToDelete, setPerfilToDelete] = useState(null);
  
  // Visibilidade das colunas
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map(col => col.accessor)
  );

  // Flag para evitar carregamento inicial duplicado
  const [isInitialized, setIsInitialized] = useState(false);

  // Carregamento inicial apenas
  useEffect(() => {
    if (!isInitialized) {
      loadPerfis();
      loadStats();
      setIsInitialized(true);
    }
  }, []);

  // Debounce para filtros - apenas quando há mudança real
  useEffect(() => {
    if (!isInitialized) return; // Não executa na inicialização

    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadPerfis();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter]);

  // Para mudanças de paginação
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Usa setTimeout para evitar conflito com o estado
    setTimeout(() => {
      loadPerfis(newPage);
    }, 0);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    // Usa setTimeout para evitar conflito com o estado
    setTimeout(() => {
      loadPerfis(1, newSize);
    }, 0);
  };

  const loadPerfis = async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        limit: limit,
        search: searchTerm,
        status: statusFilter
      };
      
      const response = await perfilService.list(params);
      console.log('Resposta do serviço:', response);
      
      setPerfis(response.data || []);
      setTotalPages(response.pagination?.totalPages || 0);
    } catch (err) {
      setError('Erro ao carregar perfis: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await perfilService.getStats();
      console.log('Stats response:', response);
      setStats(response.data || { total: 0, ativos: 0, inativos: 0 });
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  const handleDeleteClick = (perfil) => {
    setPerfilToDelete(perfil);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!perfilToDelete) return;

    try {
      await perfilService.delete(perfilToDelete.id);
      await loadPerfis();
      await loadStats();
      setShowConfirmModal(false);
      setPerfilToDelete(null);
    } catch (err) {
      setError('Erro ao excluir perfil: ' + err.message);
      setShowConfirmModal(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowConfirmModal(false);
    setPerfilToDelete(null);
  };

  const handleColumnToggle = (accessor) => {
    setVisibleColumns(prev => 
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const displayedColumns = columns.filter(col => visibleColumns.includes(col.accessor));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Perfis</h1>
          <p className="text-gray-600">Gerencie os perfis de acesso do sistema</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/seguranca/perfis/batch-permissions"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FontAwesomeIcon icon={faKey} />
            <span>Gerenciar Permissões em Lote</span>
          </Link>
          <Link
            to="/seguranca/perfis/create"
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Novo Perfil</span>
          </Link>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faUsers} className="text-2xl text-blue-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Perfis</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faUserCheck} className="text-2xl text-green-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Perfis Ativos</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.ativos}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faUserTimes} className="text-2xl text-red-500" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Perfis Inativos</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.inativos}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Filtros */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nome ou descrição..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="1">Ativo</option>
              <option value="0">Inativo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Modal de Visibilidade das Colunas */}
      <ColumnVisibilityModal
        show={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        columns={columns}
        visibleColumns={visibleColumns}
        onChange={handleColumnToggle}
      />

      {/* Controles de Paginação e Colunas */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
        <div className="order-2 sm:order-1 w-full sm:w-auto">
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
          />
        </div>
        <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            {pageSizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
            onClick={() => setShowColumnModal(true)}
            aria-label="Exibir/Ocultar colunas"
          >
            <FontAwesomeIcon icon={faColumns} />
            <span>Colunas</span>
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow">
        <Table
          columns={displayedColumns}
          data={perfis}
          loading={loading}
          emptyMessage="Nenhum perfil encontrado"
        />
      </div>

      {/* Modal de Confirmação */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o perfil "${perfilToDelete?.nome}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default PerfilsList;
